import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { HelpCircle, Target, User, ArrowRight, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";


const FAQSection = () => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/faq');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const helpfulLinks = [
    {
      title: "Distracted mind?",
      description: "Exude tranquil serenity",
      icon: HelpCircle,
      emoji: "🧘‍♀️",
      color: "bg-forest-green",
    },
    {
      title: "Cope with stress?",
      description: "Have a soothing presence",
      icon: Target,
      emoji: "🌿",
      color: "bg-soft-saffron",
    },
    {
      title: "Have Vairagya?",
      description: "Freedom in thinking",
      icon: User,
      emoji: "✨",
      color: "bg-copper-brown",
    },
    {
      title: "Disempower old patterns?",
      description: "Release from their shackles",
      icon: Sparkles,
      emoji: "🔄",
      color: "bg-forest-green",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section
      className="py-24 bg-cover bg-center bg-no-repeat relative overflow-hidden"
      style={{
        backgroundImage: "url(/bg-3.svg)",
      }}
    >
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/50"></div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="max-w-7xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-20">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 rounded-3xl bg-cloud-white flex items-center justify-center shadow-2xl">
                <Sparkles className="w-8 h-8 text-forest-green" />
              </div>
              <h2 className="font-heading text-4xl lg:text-6xl font-bold text-cloud-white">
                Looking for Answers?
              </h2>
            </div>
            {/* <p className="text-xl lg:text-2xl text-cloud-white/90 max-w-4xl mx-auto leading-relaxed">
              Explore our resources to learn more about Yoga Nidra, how to
              begin, and what to expect on your wellness journey.
            </p> */}
          </motion.div>

          {/* Cards Grid */}
          <div className="grid lg:grid-cols-4 gap-8 lg:gap-6 mb-20">
            {helpfulLinks.map((link, index) => {
              const IconComponent = link.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="group cursor-pointer"
                >
                  <div className="relative bg-cloud-white rounded-3xl p-8 lg:p-10 shadow-2xl border border-misty-sage/20 hover:border-soft-saffron/40 transition-all duration-500 hover:shadow-3xl hover:scale-105 text-center h-full overflow-hidden flex flex-col">
                    {/* Background decorative element */}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-soft-saffron/10 rounded-full blur-2xl group-hover:bg-soft-saffron/20 transition-colors duration-500"></div>

                    {/* Icon */}
                    <div className="relative z-10 mb-6">
                      <motion.div
                        className={`w-20 h-20 mx-auto rounded-3xl ${
                          link.color
                        } ${
                          link.title === "Cope with stress?" ? "text-cloud-white" : index === 1 ? "text-forest-green" : "text-cloud-white"
                        } flex items-center justify-center shadow-2xl mb-4 group-hover:scale-110 transition-transform duration-300`}
                        whileHover={{ rotate: 5 }}
                      >
                        <IconComponent className="w-10 h-10" />
                      </motion.div>
                      {/* <div className="text-4xl opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                        {link.emoji}
                      </div> */}
                    </div>

                    {/* Content */}
                    <div className="relative z-10 flex flex-col flex-grow">
                      <h3 className="font-heading text-xl lg:text-2xl font-bold text-forest-green mb-4 group-hover:text-soft-saffron transition-colors duration-300">
                        {link.title}
                      </h3>
                      <p className="text-forest-green/70 text-base lg:text-lg mb-8 leading-relaxed">
                        {link.description}
                      </p>
                      <div className="mt-auto">
                        <Link to="/faq" onClick={handleClick}>
                          <Button
                            variant="outline"
                            className="group/btn border-2 border-forest-green text-forest-green bg-cloud-white hover:text-forest-green transition-all duration-300 px-6 py-3 rounded-2xl font-semibold hover:scale-105"
                          >
                            Learn More
                            <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" />
                          </Button>
                        </Link>
                      </div>
                    </div>

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-soft-saffron/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Button */}
            {/* <motion.div variants={itemVariants} className="text-center">
              <Button className="group bg-cloud-white hover:bg-copper-brown text-forest-green hover:text-cloud-white px-12 py-6 text-xl rounded-3xl font-semibold transition-all duration-500 hover:scale-105 hover:shadow-2xl">
                <HelpCircle className="w-6 h-6 mr-3 group-hover:rotate-12 transition-transform duration-300" />
                Visit Our FAQ
                <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </motion.div> */}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
